import { useMemo, useState } from 'react'
import { Pencil } from 'lucide-react'
import {
  applyClientSideTableState,
  DataTable,
  useTableState,
  type ColumnDef,
} from '@/components/data-table'
import type { StatusHistoryEntry, StatusSnapshotEntry } from './types'
import { useStatusHistory, useStatusSnapshot } from './hooks'
import { SingleStatusEditDialog } from './components/SingleStatusEditDialog'

export function StatusesTab({ contractId }: { contractId: number }) {
  const snapshot = useStatusSnapshot(contractId)
  const history = useStatusHistory(contractId)
  const [editEntry, setEditEntry] = useState<StatusSnapshotEntry | null>(null)

  return (
    <div className="flex flex-col w-full gap-4 p-4 md:p-6">
      <section className="rounded-xl border border-slate-200 bg-white shadow-sm">
        <header className="px-6 py-4 border-b border-slate-100">
          <h3 className="text-base font-semibold tracking-tight text-slate-900">Aktualne statusy</h3>
          <p className="text-xs text-slate-500 mt-0.5">
            Równoległe tory niezależne od głównego etapu umowy. Kliknij "Edytuj" przy konkretnym torze, żeby zmienić jego wartość.
          </p>
        </header>
        <div className="px-6 py-4">
          {snapshot.isLoading && <div className="text-sm text-slate-500">Ładowanie...</div>}
          {snapshot.data && snapshot.data.length === 0 && (
            <p className="text-sm text-slate-500 italic">Brak torów statusu.</p>
          )}
          {snapshot.data && snapshot.data.length > 0 && (
            <ul className="divide-y divide-slate-100">
              {snapshot.data.map(s => (
                <li key={s.trackId} className="flex flex-wrap items-center gap-3 py-3 first:pt-0 last:pb-0">
                  <span className="text-[11px] font-semibold uppercase tracking-[0.06em] text-slate-700 w-44 shrink-0">
                    {s.trackLabelPl}
                  </span>
                  <span className="flex-1 min-w-[8rem]">
                    {s.currentValueLabelPl
                      ? <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 px-2.5 py-0.5 text-xs font-medium">
                          <span className="size-1.5 rounded-full bg-emerald-500" />
                          {s.currentValueLabelPl}
                        </span>
                      : <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-50 text-slate-500 border border-slate-200 px-2.5 py-0.5 text-xs">
                          — Nieustawiono —
                        </span>}
                  </span>
                  <span className="text-xs text-slate-500 whitespace-nowrap">
                    {s.setAt ? new Date(s.setAt).toLocaleString('pl-PL') : ''}
                    {s.setByUserName ? ` · ${s.setByUserName}` : ''}
                  </span>
                  <button
                    type="button"
                    onClick={() => setEditEntry(s)}
                    className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white text-slate-600 hover:text-sky-700 hover:border-sky-300 hover:bg-sky-50 px-2.5 py-1 text-xs font-medium transition-colors shrink-0"
                    title={`Edytuj ${s.trackLabelPl}`}
                  >
                    <Pencil size={12} />
                    Edytuj
                  </button>
                  {s.note && <span className="text-xs text-slate-500 italic w-full pl-44">{s.note}</span>}
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>

      <StatusHistorySection contractId={contractId} entries={history.data ?? []} isLoading={history.isLoading} />

      <SingleStatusEditDialog
        contractId={contractId}
        entry={editEntry}
        onClose={() => setEditEntry(null)}
      />
    </div>
  )
}

function StatusHistorySection({ contractId, entries, isLoading }: {
  contractId: number
  entries: StatusHistoryEntry[]
  isLoading: boolean
}) {
  const tableId = `contract-${contractId}-status-history`
  const { state, setSort, setSearch, setFilter, removeFilter, clearFilters } = useTableState(tableId)

  const columns: ColumnDef<StatusHistoryEntry>[] = useMemo(
    () => [
      {
        key: 'setAt',
        header: 'Data',
        sortable: true,
        filter: { kind: 'dateRange' },
        accessor: (h) => <span className="whitespace-nowrap">{new Date(h.setAt).toLocaleString('pl-PL')}</span>,
        className: 'whitespace-nowrap',
        alwaysVisible: true,
      },
      {
        key: 'trackLabelPl',
        header: 'Tor',
        sortable: true,
        filter: { kind: 'text', placeholder: 'Filtruj tor...' },
        accessor: (h) => h.trackLabelPl,
      },
      {
        key: 'valueLabelPl',
        header: 'Wartość',
        sortable: true,
        accessor: (h) => h.valueLabelPl ?? <span className="text-slate-400">— wyczyszczone —</span>,
      },
      {
        key: 'setByUserName',
        header: 'Kto zmienił',
        sortable: true,
        filter: { kind: 'text', placeholder: 'Filtruj osobę...' },
        accessor: (h) => h.setByUserName,
        className: 'whitespace-nowrap',
      },
      {
        key: 'note',
        header: 'Nota',
        accessor: (h) => <span className="italic text-slate-600">{h.note ?? ''}</span>,
      },
    ],
    [],
  )

  const filteredRows = useMemo(
    () =>
      applyClientSideTableState(entries, columns, state, {
        valueGetters: {
          setAt: (h) => h.setAt.slice(0, 10),
          trackLabelPl: (h) => h.trackLabelPl,
          valueLabelPl: (h) => h.valueLabelPl ?? '',
          setByUserName: (h) => h.setByUserName,
          note: (h) => h.note ?? '',
        },
        searchString: (h) =>
          `${h.trackLabelPl} ${h.valueLabelPl ?? ''} ${h.setByUserName} ${h.note ?? ''}`.toLowerCase(),
      }),
    [entries, columns, state],
  )

  return (
    <section className="rounded-xl border border-slate-200 bg-white shadow-sm">
      <header className="px-6 py-4 border-b border-slate-100">
        <h3 className="text-base font-semibold tracking-tight text-slate-900">Historia zmian</h3>
        <p className="text-xs text-slate-500 mt-0.5">Wszystkie zmiany statusów na torach - kto, kiedy, dlaczego.</p>
      </header>
      <div className="px-6 py-4">
        <DataTable<StatusHistoryEntry>
          tableId={tableId}
          data={filteredRows}
          columns={columns}
          rowKey={(h) => h.id}
          isLoading={isLoading}
          sort={state.sort}
          onSortChange={setSort}
          state={state}
          searchable
          searchPlaceholder="Szukaj toru / wartości / noty..."
          onSearchChange={setSearch}
          onFilterChange={setFilter}
          onFilterRemove={removeFilter}
          onClearAll={clearFilters}
          emptyState="Brak wpisów."
        />
      </div>
    </section>
  )
}
